import React, { createContext, useContext, useState, useEffect } from 'react'
import { api } from '../utils/api'

const CartContext = createContext()

export function CartProvider({ children }) {
  const [cart, setCart] = useState([])
  const [sessionId] = useState(() => {
    // Generate or retrieve session ID
    let id = localStorage.getItem('sessionId')
    if (!id) {
      id = Math.random().toString(36).substring(2) + Date.now().toString(36)
      localStorage.setItem('sessionId', id)
    }
    return id
  })

  useEffect(() => {
    loadCart()
  }, [])

  const loadCart = async () => {
    try {
      const cartData = await api.getCart(sessionId)
      setCart(cartData)
    } catch (error) {
      console.error('Failed to load cart:', error)
    }
  }

  const addToCart = async (product) => {
    const existingItem = cart.find(item => item.id === product.id)
    let newCart

    if (existingItem) {
      newCart = cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    } else {
      newCart = [...cart, { ...product, quantity: 1 }]
    }

    setCart(newCart)
    await api.updateCart(newCart, sessionId)
  }

  const removeFromCart = async (productId) => {
    const newCart = cart.filter(item => item.id !== productId)
    setCart(newCart)
    await api.updateCart(newCart, sessionId)
  }

  const updateQuantity = async (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId)
      return
    }

    const newCart = cart.map(item =>
      item.id === productId ? { ...item, quantity } : item
    )
    setCart(newCart)
    await api.updateCart(newCart, sessionId)
  }

  const clearCart = async () => {
    setCart([])
    await api.updateCart([], sessionId)
  }

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0)
  }

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0)
  }

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartCount
    }}>
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
